// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Almost every disappointing valuation comes from the same misunderstanding: the person is comparing two completely different numbers.

Your jewelry has at least three values at any moment, and they can differ by a factor of five. Once you know which is which, pricing gets much easier and much less painful.

## The three numbers

**1. Retail replacement value.** What it would cost to walk into a shop tomorrow and buy the same thing new. This is the number on your insurance valuation, and it is always the highest. It includes the shop's rent, staff, marketing, warranty and profit. It is not what anyone will pay you.

**2. Fair market resale value.** What an actual buyer will actually pay for your actual piece, today, in its current condition. This is the only number that matters when you are selling.

**3. Melt or scrap value.** The raw material value of the metal, plus whatever the stones are worth loose. This is the floor. Nothing is worth less than this, and a lot of mass market jewelry is worth exactly this.

As a very rough rule for ordinary modern jewelry, resale sits somewhere between 25 and 50 percent of retail replacement value. For antique, vintage and collectible pieces the rule breaks down completely, and resale can meet or exceed retail, because the supply is finite and there is no shop selling a new one.

## Work out the floor first: melt value

This takes two minutes and it tells you the number below which you should never sell.

**The formula:** weight in grams, times purity, times the current gold price per gram.

Purity as a decimal:

- 9k = 0.375
- 10k = 0.417
- 14k = 0.585
- 18k = 0.750
- 22k = 0.917

**Worked example.** A 12 gram 14k gold ring:

- 12 g x 0.585 = **7.02 grams of pure gold**
- At a gold price of, say, 145 dollars per gram - check the live spot price on the day, because it moves - that is just over **1,000 dollars** of gold
- A refiner or pawnbroker will pay you perhaps 70 to 90 percent of that, so realistically **710 to 915 dollars**

Two important adjustments. **Subtract the stones** from the weight, because you are not being paid for them by weight. And if the ring is set with anything meaningful, do not sell it for melt at all, because melting it destroys the thing that was actually valuable.

If you are not sure what the purity mark means, [our guide to gold marks](/blog/how-to-tell-if-gold-jewelry-is-real) decodes 375, 585, 750 and the plating abbreviations that look like gold and are not.

## What actually drives resale value up

In roughly the order of how much they matter:

1. **The stones, and what kind they are.** A genuine [old mine cut or old European cut](/blog/old-mine-cut-vs-old-european-cut-diamonds) diamond of a decent size can be worth more than everything else in the piece combined. Modern lab grown stones add very little resale value. Small melee stones add less than people expect.
2. **Metal weight and purity.** Heavy is good. 18k is worth roughly double 9k per gram.
3. **Era and desirability.** Georgian, Art Nouveau and Art Deco pieces command a premium. Mid century and 1980s gold is more of a metal value market unless it is signed.
4. **Maker or signature.** A recognised maker's mark can multiply the value several times over. Check the inside of the shank and the back of the clasp before you assume there is nothing.
5. **Condition and originality.** Original stones, original clasps, no resizing, no re-tipped claws. A piece that has never been messed with is worth more than one that has been repaired three times.
6. **Wearability.** A ring in a common size sells faster and higher than the same ring in a very small or very large size. A 16 inch necklace has a smaller audience than an 18 inch one.
7. **Completeness.** Original box, papers or receipt. Nice to have, rarely decisive on antiques, sometimes decisive on modern designer pieces.

## What does not drive value, however much we wish it did

- **What you paid for it.** The market does not care, and prices from 1998 tell you nothing.
- **Sentimental value.** Real, important, and not transferable to a buyer.
- **An old insurance valuation.** That is retail replacement, and it may be twenty years stale.
- **The brand, if the brand is a chain store.** Mass market brands generally resell at metal value.
- **Weight from a hollow construction.** Hollow chains and tube bangles look substantial and weigh very little.
- **Damage that can easily be fixed.** Buyers price in the repair, and then price in the risk.

## How to actually price your piece

Here is the method I would use, and it costs nothing.

**Step 1. Establish the floor.** Do the melt calculation above. Write the number down.

**Step 2. Find comparables that actually sold.** Not asking prices. Anyone can ask anything. Look for completed and sold listings of pieces with the same metal, similar weight, similar era, similar stones. Three to five comparables is enough to see the shape of the market.

**Step 3. Adjust honestly.** Better condition than the comparables? Price above. A replaced stone, a resized shank, a worn hallmark? Price below, and say why in the description. Buyers pay a premium for a seller who volunteers the flaws.

**Step 4. Account for the fees before you set the number.** On Nextmine, 15 percent is deducted from the sale price when the piece sells. If you need 850 dollars in your bank account, your listing price needs to be about 1,000 dollars. Work backwards from what you need, not forwards from what you hope.

**Step 5. Decide how patient you are.** Price at the top of the comparable range and you may wait months. Price at the bottom and it can sell in a day. Both are legitimate. What does not work is pricing above the whole range and then getting frustrated.

**Step 6. Leave a little room to negotiate.** Buyers on Nextmine can send you an offer. Many sellers price about 10 percent above their real target so an accepted offer still lands where they wanted.

## When to get a professional valuation

Pay for a valuation when the answer could be four figures or more, when the piece is signed by a known maker, when there is a coloured stone that might be untreated or of significant origin, or when the piece is going into an estate or a divorce settlement and the number needs to hold up.

Do not pay for a valuation on a 9k chain. The valuation will cost more than the margin you are arguing about.

Also, be clear with the valuer which number you want. Ask for **fair market value**, not insurance replacement value, or you will get a piece of paper with a large and completely unusable figure on it.

## Then decide where to sell it

The number you can get depends heavily on the route. The same antique ring can be a 250 dollar counter offer at a pawnbroker and a 900 dollar sale to a collector. [An honest comparison of where to sell](/blog/where-to-sell-pre-loved-fine-jewelry-online) goes through each option with its fees and payout speed.

Ready to put a number on it? [List your piece](/signup) and see what the circle offers. Listing is free, and you keep control of the price.

To turn weight and fineness into a melt figure without doing the arithmetic yourself, the Hallmark Society's [gold calculator](https://hallmarksociety.com/tools/gold-calculator/) will do it.
`;

export default body;
